import React, { useCallback, useEffect, useRef, useState } from "react";

import moment from "moment-timezone";
import filesize from "filesize";
import yup from "yup";
import { connect } from "react-redux";
import { compose } from "redux";
import { push } from "react-router-redux";
import Notification from "pages/_components/Notification";
import { resizableRoute } from "pages/_components/Resizable";
import Head from "pages/_components/Head";
import ContextMenu from "pages/_components/ContextMenu";
import { Box, Text, Spinner, Button, Dropdown, DropdownItem, CreatePayments } from "@ui-kit/components/index.es";

import { downloadFilelink } from "middleware/file";
import { actions as accountsActions, selectors as accountsSelectors } from "reducers/accounts";
import {
    selectors as selectorsTransactionLines,
    actions as transactionLinesActions,
} from "reducers/form/transactionLines";
import { actions as fileActions, selectors as multilineFileSelectors } from "reducers/formFields/multilineFile";
import { actions as holidaysActions, selectors as holidaysSelector } from "reducers/holidays";
import { selectors as sessionSelectors } from "reducers/session";
import { actions as thirdPaymentActions, selectors as thirdPaymentSelector } from "reducers/thirdPayment";
import { selectors as statusSelectors } from "reducers/status";
import { actions as notificationActions } from "reducers/notification";

import * as i18n from "util/i18n";
import * as configUtils from "util/config";
import * as accountUtils from "util/accounts";
import { downloadTxt } from "util/download";
import { filterForAvailableDays } from "util/date";
import { isUxDashboardImprovementsEnabled, useEmbeddedV3ShellLayoutSync } from "util/v3ShellLayoutSync";
import { arrayOf, bool, func, shape, string } from "prop-types";
import { formatFileSize } from "util/format";

const FORM_ID = "forms.newPayment.thirdParty";

const PAYMENT_TYPE_OPTIONS = [
    { value: "PH", label: "Pago de Haberes" },
    { value: "PP", label: "Pago a proveedores" },
];

NewPaymentThirdParty.propTypes = {
    isDesktop: bool.isRequired,
    dispatch: func.isRequired,
    activeEnvironment: shape({}).isRequired,
    accounts: arrayOf(shape({})),
    accessToken: string,
    fetchingAccounts: bool.isRequired,
    lastPathname: string.isRequired,
    loggedUser: string.isRequired,
    holidays: arrayOf(shape({})),
    holidaysFetching: bool.isRequired,
    summary: shape({}),
    rememberFormData: bool,
};

NewPaymentThirdParty.defaultProps = {
    accounts: [],
    accessToken: "",
    holidays: [],
    summary: {},
    rememberFormData: false,
};

function NewPaymentThirdParty(props) {
    const {
        isDesktop,
        dispatch,
        activeEnvironment,
        accounts,
        accessToken,
        fetchingAccounts,
        lastPathname,
        loggedUser,
        holidays,
        holidaysFetching,
        summary,
        rememberFormData,
    } = props;

    const [mappedAccounts, setMappedAccounts] = useState([]);
    const [maxProcessHour, setMaxProcessHour] = useState("00:00:00");
    const formikSetFieldValueRef = useRef(null);
    const hasRestoredSummaryFileRef = useRef(false);
    const fileRemovedByUserRef = useRef(false);
    const cssDropdownItem = {
        background: "transparent",
        border: "none",
        width: "100%",
        padding: 12,
        display: "flex",
        justifyContent: "center",
        borderBottom: `1px solid rgba(224, 224, 224, 1)`,
        borderRadius: 0,
        "&:hover": {
            backgroundColor: "rgba(242, 248, 253, 1)",
        },
    };

    useEffect(() => {
        setMappedAccounts(
            accounts?.map((account) => {
                const { productAlias, number: accountNumber, productType, currency, cbu } = account || {};
                return {
                    accountName: `${productAlias || ""} ${accountUtils.maskedAccountNumber(accountNumber || "")}`,
                    amount: account?.balance || 0,
                    currency: i18n.get(`currency.label.${currency || ""}`),
                    description: i18n.get(`extract.type.${productType || ""}`),
                    id: cbu || "",
                };
            }) || [],
        );
    }, [accounts]);

    useEffect(() => {
        if (!accounts.length > 0) {
            dispatch(accountsActions.listAccounts(loggedUser.userId));
        }
        if (!configUtils.getBoolean("frontend.multilinePayments.new.uploader.component")) {
            dispatch(push("/payThirdParties/newPayment"));
        }
        if (!rememberFormData) {
            dispatch(thirdPaymentActions.clearSummary());
            dispatch(fileActions.onFileRemoved());
        }
        dispatch(holidaysActions.listHolidays());
        setMaxProcessHour(configUtils.get("payment.process.hour"));
    }, []);

    const handleFormikProps = useCallback(
        (formikProps) => {
            formikSetFieldValueRef.current = formikProps?.setFieldValue ?? null;
            if (
                !fileRemovedByUserRef.current &&
                !hasRestoredSummaryFileRef.current &&
                rememberFormData &&
                summary?.file &&
                formikSetFieldValueRef.current
            ) {
                formikSetFieldValueRef.current("file", summary.file);
                hasRestoredSummaryFileRef.current = true;
            }
        },
        [rememberFormData, summary?.file],
    );

    const handleFileRemoved = useCallback(() => {
        fileRemovedByUserRef.current = true;
        dispatch(fileActions.onFileRemoved());
        dispatch(transactionLinesActions.clearTransactionLines());
        if (summary?.file) {
            dispatch(
                thirdPaymentActions.setSummaryNewThirdPayment({
                    ...summary,
                    file: null,
                }),
            );
        }
    }, [dispatch, summary]);

    const setHourToDate = (date) => {
        const hours = maxProcessHour.split(":");
        return date && hours.length === 3
            ? moment(date).set({ hour: hours[0], minute: hours[1], second: hours[2] })
            : moment(date);
    };

    const getMinDate = () => {
        const date = moment.tz("America/Argentina/Buenos_Aires");
        const today = date.clone().toDate();
        const maxTime = new Date(setHourToDate(today));
        if (today > maxTime) {
            date.add(+1, "days");
        }

        while (!filterForAvailableDays(holidays, date)) {
            date.add(+1, "days");
        }

        return date.toDate();
    };

    const handleBack = () => {
        if (lastPathname === "/v3/dashboard") {
            dispatch(push(lastPathname));
        } else {
            dispatch(push("/payThirdParties/list"));
        }
    };

    const handleSubmit = (valuesForm) => {
        const selectedAccount = accounts?.find((account) => account.cbu === valuesForm.debitAccount);
        const dateProcessAR = valuesForm?.processDate
            ? moment
                  .tz(
                      moment(valuesForm?.processDate).format("YYYY-MM-DD"),
                      "YYYY-MM-DD",
                      "America/Argentina/Buenos_Aires",
                  )
                  .toDate()
            : valuesForm?.processDate;
        dispatch(fileActions.onFileProcess({ ...valuesForm?.file }));
        dispatch(
            thirdPaymentActions.summaryThirdPay(
                {
                    account: selectedAccount,
                    file: [{ fileId: parseInt(valuesForm?.file?.idFile, 10), ...valuesForm?.file }],
                    typePayment: valuesForm?.paymentType?.value,
                    dateProcess: dateProcessAR,
                    description: valuesForm?.reference,
                },
                { ...valuesForm?.file?.totalAmount },
                true,
            ),
        );
        dispatch(thirdPaymentActions.setSummaryNewThirdPayment(valuesForm));
    };

    const handleDownload = async (formatDownload) => {
        if (formatDownload.includes("PH")) {
            const { data } = await downloadFilelink("6");
            downloadTxt("archivoEjemploHAB.txt", data.data.content);
        } else {
            const { data } = await downloadFilelink("7");
            downloadTxt("archivoEjemploPRO.txt", data.data.content);
        }
    };

    const renderDownloadButton = () => (
        <Dropdown
            w="100%"
            direction="right"
            optionWidth="200px"
            renderWithPortal
            label={
                <Button
                    isInactive={false}
                    aria-label={i18n.get(`${FORM_ID}.download.example`)}
                    text={i18n.get(`${FORM_ID}.download.example`)}
                    variant="outline"
                    icon="SendDown"
                    iconVariant="outline"
                    w="200px"
                />
            }>
            <DropdownItem>
                <Button
                    onClick={() => {
                        handleDownload("PH");
                    }}
                    type="button"
                    text={<Text variant="body2">{i18n.get("formats.txtHaberes")}</Text>}
                    css={cssDropdownItem}
                />
                <Button
                    onClick={() => {
                        handleDownload("PP");
                    }}
                    type="button"
                    text={<Text variant="body2">{i18n.get("formats.txtProveedores")}</Text>}
                    css={cssDropdownItem}
                />
            </DropdownItem>
        </Dropdown>
    );

    const getMobileNewPaymentShellMenu = () => (
        <ContextMenu
            isDesktop={isDesktop}
            color="white"
            buttonClassName="align-self-center account-dropdown-header-font font-black-alpha ml-2"
            items={[
                {
                    label: "payments.payThirdParties.newPayment.fileSupplierPayments.label",
                    onClick: () => handleDownload("PH"),
                    icon: "SendDown",
                },
                {
                    label: "payments.payThirdParties.newPayment.fileSalaryPayments.label",
                    onClick: () => handleDownload("PP"),
                    icon: "SendDown",
                },
            ]}
        />
    );

    const validateFile = ({ invalidLines = 0, validLines = 0 } = {}) => {
        const totalLines = invalidLines + validLines;
        if (totalLines === 0) {
            return false;
        }
        const invalidPercentage = (invalidLines * 100) / totalLines;

        return (
            invalidPercentage > configUtils.getInteger("forms.fields.multilinefile.minimum.invalid.percentage.allowed")
        );
    };

    useEmbeddedV3ShellLayoutSync(dispatch, {
        embeddedInV3Shell: isUxDashboardImprovementsEnabled(),
        isDesktop,
        titleKey: "payments.payThirdParties.newPayment.title",
        goBackPath: "/payThirdParties/list",
        scopes: ["form"],
        rightContentMobile: getMobileNewPaymentShellMenu,
    });

    const handleFileFinished = (idFile, message, validLines, invalidLines, totalLines) => {
        const maxBatchLines = configUtils.getInteger("multilinePayments.transactionLinesBatch.count", 1000);
        const invalidFile = validateFile({ validLines, invalidLines });
        const validBatch = invalidLines + validLines > maxBatchLines;
        if (validLines === totalLines && totalLines > 0) {
            dispatch(
                notificationActions.showNotification(
                    i18n.get(`${FORM_ID}.uploadFile.success`, null, {
                        VALID_LINES: validLines,
                        TOTAL_LINES: totalLines,
                    }),
                    "success",
                    ["form"],
                ),
            );
        }
        if (!invalidFile && validBatch) {
            dispatch(
                notificationActions.showNotification(
                    i18n.get("forms.notification.uploadFile.fail.batch.limit.reached", "", {
                        count: maxBatchLines,
                    }),
                    "warning",
                    ["form"],
                ),
            );
        } else if (invalidLines > 0) {
            dispatch(
                notificationActions.showNotification(
                    i18n.get(`${FORM_ID}.uploadFile.success`, null, {
                        VALID_LINES: validLines,
                        TOTAL_LINES: totalLines,
                    }),
                    "warning",
                    ["form"],
                ),
            );
        } else if (!idFile) {
            dispatch(
                notificationActions.showNotification(
                    i18n.get("forms.inputFile.massivePayments.invalid.file.type"),
                    "warning",
                    ["form"],
                ),
            );
        }
    };

    const handleFileError = (error) => {
        if (error?.errorMessage.includes("File size exceeds maximum allowed size of")) {
            dispatch(
                notificationActions.showNotification(
                    i18n
                        .get("file.upload.input.labelMaxFileSize")
                        .replace("{filesize}", formatFileSize(configUtils.getInteger("core.maxFileSize"))),
                    "error",
                    ["form"],
                ),
            );
        } else {
            dispatch(
                notificationActions.showNotification(
                    error?.NO_FIELD || i18n.get("forms.inputFile.massivePayments.invalid.file.type"),
                    "error",
                    ["form"],
                ),
            );
        }
    };

    return (
        <>
            {!isUxDashboardImprovementsEnabled() && (
                <Notification scopeToShow="form" notificationClassname={!isDesktop ? "z-index-9" : ""} />
            )}

            <div className={`admin-detail-head ${isDesktop ? "px-4" : "d-block"}`}>
                {isDesktop && <Head onBack={handleBack} />}
                {!(isUxDashboardImprovementsEnabled() && !isDesktop) && (
                    <Head
                        accessibilityTextId="payments.payThirdParties.newPayment.title"
                        headerClassName={
                            !isDesktop ? "blue-main-header-mobile blue-main-title-mobile background-transparent" : ""
                        }
                        title="payments.payThirdParties.newPayment.title"
                        titleClassName={
                            !isDesktop
                                ? "header-mobile-title-background-blue white-space-normal align-center"
                                : undefined
                        }
                        onBack={!isDesktop && handleBack}
                        rightContent={() => {
                            if (activeEnvironment?.permissions?.newThirdPayment) {
                                if (isDesktop) {
                                    return renderDownloadButton();
                                }

                                return (
                                    <ContextMenu
                                        isDesktop={isDesktop}
                                        color="white"
                                        buttonClassName="align-self-center account-dropdown-header-font font-black-alpha ml-2"
                                        items={[
                                            {
                                                label: "payments.payThirdParties.newPayment.fileSupplierPayments.label",
                                                onClick: () => handleDownload("hab"),
                                                icon: "SendDown",
                                            },
                                            {
                                                label: "payments.payThirdParties.newPayment.fileSalaryPayments.label",
                                                onClick: () => handleDownload("pro"),
                                                icon: "SendDown",
                                            },
                                        ]}
                                    />
                                );
                            }
                            return null;
                        }}
                        messageTooltip={isDesktop ? "payments.payThirdParties.newPayment.title.help" : undefined}
                        alwaysShowMessage={isDesktop}
                    />
                )}
                {holidaysFetching || fetchingAccounts ? (
                    <Box flex justify="center" align="center" column py={72}>
                        <Spinner size="large" />
                    </Box>
                ) : (
                    <Box mt={isDesktop ? 32 : 0}>
                        <CreatePayments
                            initialValues={summary}
                            onSubmit={handleSubmit}
                            setFormikProps={handleFormikProps}
                            validationSchema={yup.object().shape({
                                debitAccount: yup.string().required(i18n.get("transfers.validation.required")),
                                reference: yup
                                    .string()
                                    .required(i18n.get("transfers.validation.required"))
                                    .min(3, `${i18n.get("forms.validation.min.required")} de 3 caracteres`),
                                paymentType: yup.string().required(i18n.get("transfers.validation.required")),
                                file: yup.object().required(i18n.get("transfers.validation.required")),
                            })}
                            getFormLabel={(id) =>
                                i18n.get(`${FORM_ID}.${id}`, "", {
                                    MAX_FILE_SIZE: filesize(configUtils.getInteger("core.maxFileSize", 5242880)),
                                })
                            }
                            loading={holidaysFetching}
                            loadingSubmit={holidaysFetching}
                            paymentTypeOptions={PAYMENT_TYPE_OPTIONS}
                            accountsOptions={mappedAccounts}
                            fileTypeOptions={[]}
                            processDateTooltipText={i18n.get("payments.payThirdParties.newPayment.date.help", "", {
                                HORA_PROCESO: maxProcessHour.split(":")[0],
                            })}
                            idActivity="files.upload"
                            idFormField="file"
                            accessToken={accessToken}
                            idEnvironment={activeEnvironment?.id}
                            minProcessDate={getMinDate()}
                            maxFileSize={configUtils.getInteger("core.maxFileSize")}
                            validateFile={validateFile}
                            enableFileTypeOptions={configUtils.getBoolean(
                                "frontend.payments.thirdparties.ADAPTADO.functionalities",
                                false,
                            )}
                            onFileProcessFinished={handleFileFinished}
                            onFileProcessError={handleFileError}
                            onFileRemoved={handleFileRemoved}
                            filesTypesToAccept={["text/plain"]}
                            downloadTxt={downloadTxt}
                        />
                    </Box>
                )}
            </div>
        </>
    );
}

const mapStateToProps = (state) => ({
    activeEnvironment: sessionSelectors.getActiveEnvironment(state),
    accounts: accountsSelectors.getAccounts(state),
    fetchingAccounts: accountsSelectors.getFetching(state),
    loggedUser: sessionSelectors.getUser(state),
    itemsDetail: selectorsTransactionLines.getTransactionLines(state),
    holidays: holidaysSelector.getHolidays(state),
    holidaysFetching: holidaysSelector.isFetching(state),
    summary: thirdPaymentSelector.getSummaryNewPayment(state),
    rememberFormData: thirdPaymentSelector.getRememberFormData(state),
    processedFileData: multilineFileSelectors.getProcessedFileData(state),
    lastPathname: statusSelectors.getPathname(state),
    accessToken: sessionSelectors.getAccessToken(state),
});

export default compose(connect(mapStateToProps), resizableRoute)(NewPaymentThirdParty);
